import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Badge } from "./ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (n) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ label, value }) => (
  <div className="p-3 rounded-lg border bg-stone-50 border-stone-200 text-center">
    <p className="text-xl font-bold text-stone-900">{value}</p>
    <p className="text-xs text-stone-500">{label}</p>
  </div>
);

const EtifoodDdtImportDialog = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(1); setPreview(null); setResult(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/etifood-sales/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setPreview(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella lettura del file");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/etifood-sales/import/confirm`, { session_id: preview.session_id }, { timeout: 180000 });
      setResult(r.data);
      setStep(3);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'import");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#8A1C2A]" />Importa DDT da Etifood</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-stone-600">Carica l'export Excel <span className="font-mono">DDT DETTAGLIO</span> di Etifood. I DDT già presenti vengono <b>aggiornati automaticamente</b> se nel gestionale sono cambiati (prezzi inseriti dopo, correzioni) — mai doppioni.</p>
            <label className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-[#8A1C2A]/50 hover:bg-stone-50 transition-colors" data-testid="ddt-dropzone">
              <Upload className="w-8 h-8 text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{busy ? "Lettura del file…" : "Clicca per scegliere il file .xls / .xlsx"}</span>
              <input type="file" accept=".xls,.xlsx" className="hidden" disabled={busy} onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="ddt-file-input" />
            </label>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCard label="DDT trovati" value={preview.ddt_count} />
              <StatCard label="Righe" value={preview.lines_count} />
              <StatCard label="Kg totali" value={fmtEur(preview.total_kg)} />
              <StatCard label="Netto €" value={fmtEur(preview.total_net)} />
            </div>
            <p className="text-sm text-stone-600">Periodo: <b>{preview.date_from?.split("-").reverse().join("/")}</b> → <b>{preview.date_to?.split("-").reverse().join("/")}</b> · {preview.customers_count} clienti · {preview.already_imported} già importati</p>
            {preview.unlinked_customers?.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="ddt-unlinked-warning">
                <p className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{preview.unlinked_customers.length} clienti non trovati nell'app (senza agente)</p>
                <p className="text-amber-700 mt-1">Verranno importati comunque: potrai collegarli manualmente dall'archivio. {preview.unlinked_customers.slice(0, 6).join(", ")}{preview.unlinked_customers.length > 6 ? "…" : ""}</p>
              </div>
            )}
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <div className="max-h-56 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">N.</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Data</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Cliente</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Righe</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Netto €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.docs.map((d) => (
                      <tr key={`${d.number}-${d.date}`} className={`border-t border-stone-100 ${d.already_imported ? "opacity-40" : ""}`}>
                        <td className="p-2 font-semibold">{d.number}{d.already_imported && <Badge variant="outline" className="ml-1 text-[9px]">GIÀ</Badge>}</td>
                        <td className="p-2">{d.date?.split("-").reverse().join("/")}</td>
                        <td className="p-2 truncate max-w-[180px]">{d.customer_name}</td>
                        <td className="p-2 text-right">{d.lines}</td>
                        <td className="p-2 text-right">{fmtEur(d.total_kg)}</td>
                        <td className="p-2 text-right">{fmtEur(d.total_net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-6 h-6" /><p className="font-semibold">Import completato</p></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ddt-result-cards">
              <StatCard label="DDT nuovi" value={result.ddt_created} />
              <StatCard label="DDT aggiornati" value={result.ddt_updated ?? 0} />
              <StatCard label="Invariati (già presenti)" value={result.ddt_unchanged ?? result.ddt_skipped} />
              <StatCard label="Righe importate" value={result.lines_imported} />
              <StatCard label="Clienti da collegare" value={result.customers_unlinked} />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && <Button variant="outline" onClick={reset} data-testid="ddt-back-btn">Indietro</Button>}
          {step === 2 && (
            <Button onClick={confirm} disabled={busy} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="ddt-confirm-btn">
              {busy ? "Import in corso…" : "Conferma import"}
            </Button>
          )}
          {step === 3 && <Button onClick={close} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="ddt-done-btn">Chiudi</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EtifoodDdtImportDialog;
